import { useState, type FormEvent, useMemo } from 'react'

import { API_BASE, errorMessage, isApiError } from '@/api/client'
import {
  useApprovePack,
  useDispatchPack,
  useMatterPack,
  useRenderPack,
  type AssessmentPack,
} from '@/api/pack'
import {
  ABANDONED_COPY,
  ACTION_COPY,
  ACTION_TONE,
  formatWorklistEffort,
  MODEL_CALL_COPY,
  RUN_ROW_LABELS,
  modelCallState,
  NO_DRAFT_COPY,
  formatDate,
  formatDuration,
  formatWhen,
  OUTCOME_TONE,
  orderedBlocks,
  SEVERITY_TONE,
  stageLabel as stageGloss,
  useCloseMatter,
  useCorrectDeadline,
  useOverrideDeadline,
  useMatter,
  usePipelineRun,
  usePipelineRuns,
  useRunLedger,
  useVerifyDeadline,
  VERDICT_COPY,
} from '@/api/pipeline'
import {
  GATE_SEVERITIES,
  JURISDICTION_OFFICES,
  WORKLIST_ACTIONS,
  jurisdictionLabel,
  lifecycleLabel,
  proceedingLabel,
  sourceChannelLabel,
  type DeadlineReconciliation,
  type GateFinding,
  type Jurisdiction,
  type MatterDetail,
  type MatterEvent,
  type PipelineRun,
  type PipelineRunSummary,
  type PipelineStageResult,
  type RetrievalBundleSummary,
} from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import { DraftView } from '@/components/DraftView'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  KeyValues,
  ScreenHeader,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Textarea,
  cn,
  useToast,
} from '@/components/ui'
import { useRouter } from '@/routes'
import { useForgetMissingMatter } from '@/useForgetMissingMatter'

import { NextStepCard } from './NextStepCard'
import { RerunCard } from './RerunCard'
import { ResearchCard } from './ResearchCard'
import { SignsCard } from './SignsCard'
import { SpecificationCard } from './SpecificationCard'
import { parseCasePath } from './casePath'
import { CASE_ANCHORS } from './nextStep'
import { LoadingLine, RunErrorCard } from './runParts'
import { eventLabel, stageLabel } from './stageLabels'

/**
 * Case — one run, read top to bottom, by somebody asking "why is there no
 * draft?".
 *
 * That question is the whole design brief. The first live draft this platform
 * produced was generated over a blocking `DEADLINE_MISMATCH` and asserted three
 * facts nobody supplied, and the reason nobody noticed is that the guards'
 * answers existed only as return values (`artifacts/run_log.json`; the runner's
 * docstring). Every refusal the pipeline makes is therefore on this screen, in
 * the order the pipeline made it, whether or not it stopped the run:
 *
 * Card titles below are what an attorney would call them; the invariant each
 * one carries is named here, in the code, rather than printed on screen.
 *
 * | Card | What it answers | Invariant |
 * |------|-----------------|-----------|
 * | How this run ended | what became of the matter, and may it be sent | I4 |
 * | What ran, step by step | which stage stopped, and what it found | — |
 * | The two deadline dates | both dates, the rule, whether it BLOCKs | I7 |
 * | What the law library gave us | what the corpus gave, including the blocks it did not | — |
 * | What the checks found | every finding by severity, BLOCK first | I4 |
 * | What needs doing before this goes out | the CONSIDER lines, deterministically | I4 |
 * | Draft | the text, and whether it may leave the building | I4 |
 * | Event log | what was appended, in append order | I12 |
 *
 * ## Nothing here is recomputed
 *
 * `exportable`, `blocks`, `verdict` and the severity of every finding are read
 * off the response as the server decided them. This screen has no rule of its
 * own about what may be sent, because a screen with such a rule is a second
 * gate and the second gate is always the lenient one. Where copy interprets —
 * the verdict glosses in `VERDICT_COPY` — it interprets a value, never replaces
 * one.
 *
 * ## A BLOCK is the gate working
 *
 * A blocked run keeps its draft and shows it, marked not exportable, next to
 * the findings that refuse it. It is presented as a worklist, not as an error:
 * the draft is work product, the reviewer needs to see what was written, and
 * the only way past a BLOCK is a recorded override by a person (I4, I12) —
 * which is P5's control and is deliberately not drawn here as a button.
 *
 * ## Two readers, in the right order
 *
 * Everything above was written for the person auditing the pipeline, and it
 * held. What it cost was reported plainly: *"the flow is still stuck and its
 * not very easy to understand flow, attorney using first time will be lost in
 * technical flow"*. Ten cards of stage names, flag vocabulary and invariant
 * numbers, and no sentence anywhere saying what to do.
 *
 * So the page now opens with {@link NextStepCard} — one headline, one
 * paragraph, one button, derived in `nextStep.ts` from values the server
 * already decided — and the engine's own record moves below it, intact, under
 * a disclosure. **Nothing was deleted.** A refusal nobody can inspect is how
 * the first live draft went out over a blocking deadline; the record is one
 * click away rather than the first thing an attorney has to parse.
 *
 * {@link RerunCard} closes the other half of the report. Correcting a fact and
 * asking for another run are one motion in an attorney's head, and this screen
 * implemented only the first — so clearing a blocker read as having done
 * nothing at all.
 */
export function CaseScreen() {
  const router = useRouter()
  const { matterId, runId: pinnedRunId } = useMemo(
    () => parseCasePath(router.path),
    [router.path],
  )

  const matter = useMatter(matterId)
  useForgetMissingMatter(matterId, matter)
  const runs = usePipelineRuns(matterId)
  // The newest run unless the URL pins one, so `#/case/GW-2026-0001/<run>` is a
  // link to a specific run somebody can send while `#/case/GW-2026-0001` always
  // shows the latest.
  const selectedRunId = pinnedRunId ?? runs.data?.[0]?.id ?? null
  const run = usePipelineRun(selectedRunId)

  if (!matterId) {
    return (
      <EmptyState
        title="No matter selected"
        description="Case reads one matter's runs. Open one from the Queue, or start a new one."
      >
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => router.navigate('/queue')}>
            Go to Queue
          </Button>
          <Button variant="secondary" onClick={() => router.navigate('/new')}>
            New case
          </Button>
        </div>
      </EmptyState>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <MatterHeader matterId={matterId} matter={matter.data} isPending={matter.isPending} />

      {matter.isError ? <RunErrorCard title="Could not load this matter" error={matter.error} /> : null}

      {/* Written for the attorney, and first on purpose. Everything below it is
          the record the pipeline wrote for whoever comes to audit the refusal. */}
      <NextStepCard run={run.data} matter={matter.data} />

      {matter.data ? <DeadlineSignature matter={matter.data} /> : null}

      <RunPicker
        runs={runs.data ?? []}
        isPending={runs.isPending}
        error={runs.isError ? runs.error : null}
        selectedRunId={selectedRunId}
        matterId={matterId}
      />

      {runs.isSuccess && (runs.data ?? []).length === 0 ? (
        <EmptyState
          title="This matter has never been run"
          description="A matter with no run is not a failure — nothing has asked the pipeline about it yet. New case creates a matter and starts its first run in one submit."
        />
      ) : null}

      {run.isPending && selectedRunId ? (
        <Card>
          <LoadingLine label="Loading the run…" />
        </Card>
      ) : null}
      {run.isError ? <RunErrorCard title="Could not load this run" error={run.error} /> : null}
      {run.data ? <RunReport run={run.data} jurisdiction={matter.data?.jurisdiction} /> : null}

      <SignsCard matterId={matterId} />
      <ResearchCard matterId={matterId} />
      <SpecificationCard
        matterId={matterId}
        isSpecificationMatter={run.data?.kind === 'specification'}
      />

      {/* The other half of every fix on this page: having corrected a fact, ask
          the pipeline to look again. */}
      <RerunCard
        matter={matter.data}
        run={run.data}
        neverRun={runs.isSuccess && (runs.data ?? []).length === 0}
      />

      {/* Last, and deliberately: ending a matter is the least common thing to
          do here and the only one that cannot be undone. */}
      {matter.data ? <CloseMatter matter={matter.data} /> : null}

      <EventLogCard matter={matter.data} isPending={matter.isPending} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// The matter
// ---------------------------------------------------------------------------

function MatterHeader({
  matterId,
  matter,
  isPending,
}: {
  matterId: string
  matter: MatterDetail | undefined
  isPending: boolean
}) {
  const kicker = matter
    ? `${proceedingLabel(matter.proceeding_type)} · ${jurisdictionLabel(matter.jurisdiction)} · ${sourceChannelLabel(matter.source_channel)}`
    : 'matter'

  const subline = matter
    ? [
        matter.application_number ? `Application ${matter.application_number}` : null,
        matter.mark_text,
        matter.applicant_name,
        `Status: ${lifecycleLabel(matter.lifecycle)}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : isPending
      ? 'Loading the matter…'
      : 'The run below is what the pipeline recorded for this matter.'

  return (
    <div className="flex flex-col gap-3">
      <ScreenHeader kicker={kicker} title={matterId} description={subline} />
    </div>
  )
}

/**
 * Ending a matter nobody is going to take further.
 *
 * The reported state was *"no way i can edit and move further, delete the
 * matter, i am just stuck with it"*, and the missing control was this one:
 * everything else on this screen assumes the matter is being worked.
 *
 * Three things this deliberately is not.
 *
 * It is **not a delete**, and no amount of asking will make it one. The event
 * log refuses UPDATE and DELETE at the database, and the matter row sits behind
 * ON DELETE RESTRICT. A matter opened in error cannot be un-opened; this is the
 * answer to that, not an exception to it.
 *
 * It is **not a decline**. The four decline reasons are findings the machine
 * made about the papers, and DECLINED counts as a *pass* in the queue and the
 * metrics. Filing a person's decision under one of them would misreport both
 * who decided and why.
 *
 * It is **not undoable**, and the copy says so before the click rather than in
 * a toast afterwards.
 */
function CloseMatter({ matter }: { matter: MatterDetail }) {
  const { hasScope } = useAuth()
  const toast = useToast()
  const close = useCloseMatter()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const terminal = matter.lifecycle === 'CLOSED' || matter.lifecycle === 'DECLINED'

  if (!hasScope('matters:write')) return null

  if (terminal) {
    return (
      <Card role="group" aria-label="Matter closed" className="gap-2">
        <CardHeader
          kicker="Ended"
          title={`This matter is ${matter.lifecycle}`}
          description="Its state does not move any more. A note can still be recorded against it — that is how a correction is made to a finished matter."
          actions={<Badge tone="warn">{matter.lifecycle}</Badge>}
        />
      </Card>
    )
  }

  async function onClose(event: FormEvent) {
    event.preventDefault()
    if (!reason.trim()) return
    try {
      await close.mutateAsync({ matterId: matter.id, reason: reason.trim() })
      setOpen(false)
      setReason('')
      toast.push('Matter closed', {
        tone: 'success',
        description: 'Recorded with your name and your reason. It cannot be reopened.',
      })
    } catch (error) {
      toast.push('Could not close the matter', {
        tone: 'error',
        description: errorMessage(error),
      })
    }
  }

  return (
    <Card role="group" aria-label="Close this matter" className="gap-2">
      <CardHeader
        kicker="Close"
        title="End this matter"
        description="For a matter nobody is going to take further — opened in error, client not proceeding, handled elsewhere."
      />
      {open ? (
        <form className="flex flex-col gap-3" onSubmit={onClose}>
          <Field
            label="Why is this matter ending?"
            hint="Required. It is what somebody reading the log a year from now has instead of the conversation you are having today."
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                rows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Client is not proceeding with the UK designation."
              />
            )}
          </Field>
          <p className="m-0 text-micro text-block-fg">
            This cannot be undone. Nothing here deletes the matter — the record and
            everything already run against it stay exactly as they are; the matter
            simply stops being worked.
          </p>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={close.isPending || !reason.trim()}>
              {close.isPending ? 'Closing…' : 'Close this matter'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={close.isPending}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
            Close this matter…
          </Button>
        </div>
      )}
    </Card>
  )
}

/**
 * The matter-level deadline: what it is, correcting it, and signing it (REQ-1).
 *
 * Agreement between the two dates is **not** clearance: the mockup keeps a
 * BLOCK on a matter whose dates agree but whose signature is missing, and that
 * is I7's second sentence drawn. So this reports `deadline_verified` on its own
 * terms and never softens it because the reconciliation agreed.
 *
 * This card used to say "P3 builds that control, so it is not a button here",
 * and the caution behind that sentence was right: a button that LOOKED like
 * the attorney signature and was not would be the worst thing on this screen.
 * P3 is now, and these are the real controls — `facts.corrected` and
 * `deadline.verified`, both append-only, both refused to a machine credential
 * by `HUMAN_ONLY_EVENTS`, both projecting through the same path a replay would.
 *
 * They exist because without them a matter was a **dead end**: a GB refusal
 * whose deadline nothing could read reached `DEADLINE_UNKNOWN`, blocked, and
 * then had no control anywhere in the application that could move it. Reading
 * the date off the document (`documents/deadline.py`) fixes the common case;
 * this fixes the case where the document does not print one.
 *
 * Correcting a *signed* date deliberately un-signs it — the projection clears
 * `deadline_verified` whenever the date actually moves, because a signature
 * given for one date is not a signature for another. The copy says so before
 * the click rather than after it.
 */
/** Today as `YYYY-MM-DD`, in the viewer's own timezone.
 *
 * Compared as strings against `matter.deadline_date`, which the API sends in
 * the same shape. This only decides whether a *control* is offered — the server
 * makes the real judgement with its own clock, passed explicitly into the
 * reconciler so a replay reproduces it (I12). A browser an hour either side of
 * midnight showing or hiding the button a day early is not a correctness
 * question; a server reading its own clock inside a stage would be.
 */
function todayISO(): string {
  const now = new Date()
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')
}


/**
 * Was this deadline signed once and since un-signed?
 *
 * Read off the log rather than guessed: a `deadline.verified` event with the
 * matter now unverified can only mean the projection cleared it
 * (`_clear_verification_if_date_moves`) — nothing else ever sets the column
 * back.
 */
function signatureWasCleared(matter: MatterDetail): boolean {
  return (
    !matter.deadline_verified && matter.events.some((entry) => entry.event === 'deadline.verified')
  )
}

function DeadlineSignature({ matter }: { matter: MatterDetail }) {
  const { hasScope } = useAuth()
  const toast = useToast()
  const correct = useCorrectDeadline()
  const verify = useVerifyDeadline()
  const override = useOverrideDeadline()
  const [editing, setEditing] = useState(false)
  const [draftDate, setDraftDate] = useState(matter.deadline_date ?? '')
  const [overriding, setOverriding] = useState(false)
  const [reason, setReason] = useState('')
  const canWrite = hasScope('matters:write')
  const busy = correct.isPending || verify.isPending || override.isPending
  // Only offered where it does something. The override clears exactly one flag,
  // and a deadline that has not passed does not carry it — putting the control
  // on every matter would invite a person to record a decision about nothing.
  const lapsed = matter.deadline_date != null && matter.deadline_date < todayISO()
  // A finished matter's state does not move — `_refuse_if_terminal` in
  // `routers/matters.py` answers 409 for any projecting event on one. Hiding
  // the controls is not the guard; the server is. This just stops the screen
  // offering an action that cannot succeed.
  const terminal = matter.lifecycle === 'CLOSED' || matter.lifecycle === 'DECLINED'

  async function onSave(event: FormEvent) {
    event.preventDefault()
    if (!draftDate) return
    try {
      await correct.mutateAsync({ matterId: matter.id, deadlineDate: draftDate })
      setEditing(false)
      toast.push('Deadline recorded', {
        tone: 'success',
        description:
          matter.deadline_verified && draftDate !== matter.deadline_date
            ? 'The signature was cleared: it was given for the previous date.'
            : 'Added to the log. It still needs an attorney signature.',
      })
    } catch (error) {
      toast.push('Could not record the deadline', {
        tone: 'error',
        description: errorMessage(error),
      })
    }
  }

  async function onOverride(event: FormEvent) {
    event.preventDefault()
    if (!reason.trim()) return
    try {
      await override.mutateAsync({ matterId: matter.id, reason: reason.trim() })
      setOverriding(false)
      setReason('')
      toast.push('Recorded — this matter may proceed', {
        tone: 'success',
        description:
          'Recorded in the log against your name. The date is unchanged: if an extension moved it, correct the date as well.',
      })
    } catch (error) {
      toast.push('Could not record the override', {
        tone: 'error',
        description: errorMessage(error),
      })
    }
  }

  async function onVerify() {
    try {
      await verify.mutateAsync({
        matterId: matter.id,
        deadlineDate: matter.deadline_date,
      })
      toast.push('Deadline signed', {
        tone: 'success',
        description: 'Recorded in the log against your name. The matter can now be approved.',
      })
    } catch (error) {
      toast.push('Could not sign the deadline', {
        tone: 'error',
        description: errorMessage(error),
      })
    }
  }

  return (
    <Card
      id={CASE_ANCHORS.deadline}
      tabIndex={-1}
      role="group"
      aria-label="Response deadline"
      className="gap-2"
    >
      <CardHeader
        kicker="Deadline"
        title={formatDate(matter.deadline_date)}
        description="The date this matter is worked to. Everything else waits on it: nothing is drafted against a deadline nobody has confirmed."
        actions={
          matter.deadline_verified ? (
            <Badge tone="ok">verified by analyst</Badge>
          ) : (
            <Badge tone="block">BLOCK — unverified</Badge>
          )
        }
      />
      <p className="m-0 text-micro text-muted">
        {matter.deadline_verified
          ? // Not "permanent": a signature attests to one date, and the record
            // clears it the moment a correction or a later run moves that date.
            // Saying otherwise was TM-10 — a tester re-ran a signed matter, the
            // date moved, and the badge flipped back with the page promising
            // it never would.
            'An attorney has checked this date against the papers and signed it. The signature stays on the record and is never overwritten — but it belongs to this date: if a correction or a later run moves the date, it is cleared and has to be given again.'
          : signatureWasCleared(matter)
            ? 'This matter was signed before, and the signature was cleared when the date moved — a signature given for one date is not one for another. Check the new date against the papers and sign it again.'
            : 'Dates agreeing is not the same as a deadline being verified. Only a person can sign it — an automated credential cannot — so sign it below once you have checked it against the papers.'}
      </p>

      {terminal ? (
        <p className="m-0 text-micro text-muted">
          This matter is {matter.lifecycle} and its state does not move, so the
          deadline can no longer be changed or signed here.
        </p>
      ) : !canWrite ? (
        <p className="m-0 text-micro text-muted">
          Changing or signing the deadline needs the matters:write scope, which this
          credential does not hold.
        </p>
      ) : editing ? (
        <form className="flex flex-wrap items-end gap-3" onSubmit={onSave}>
          <Field
            label="Deadline as it should be"
            hint="From the papers, or from the office if the papers do not print one."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="date"
                value={draftDate}
                onChange={(event) => setDraftDate(event.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" disabled={busy || !draftDate}>
            {correct.isPending ? 'Recording…' : 'Record the date'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setDraftDate(matter.deadline_date ?? '')
              setEditing(false)
            }}
          >
            Cancel
          </Button>
          {matter.deadline_verified ? (
            <p className="m-0 w-full text-micro text-block-fg">
              This deadline is signed. Changing the date clears the signature — it was
              given for {formatDate(matter.deadline_date)}, not for a different day.
            </p>
          ) : null}
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setDraftDate(matter.deadline_date ?? '')
              setEditing(true)
            }}
          >
            {matter.deadline_date ? 'Correct the date' : 'Set the date'}
          </Button>
          {matter.deadline_date && !matter.deadline_verified ? (
            <Button type="button" variant="primary" disabled={busy} onClick={onVerify}>
              {verify.isPending ? 'Signing…' : 'I have checked this date — sign it'}
            </Button>
          ) : null}
          {lapsed ? (
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setOverriding(true)}
            >
              This deadline has passed — proceed anyway
            </Button>
          ) : null}
          {!matter.deadline_date ? (
            <span className="text-micro text-muted">
              There is no date to sign yet. Nothing was read off the papers and no rule
              applies at this office.
            </span>
          ) : null}
        </div>
      )}

      {overriding && !terminal && canWrite ? (
        <form className="flex flex-wrap items-end gap-3" onSubmit={onOverride}>
          <Field
            label="Why this matter proceeds anyway"
            hint="An extension, a restoration, a demonstration. Recorded against your name in the log, permanently."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                value={reason}
                placeholder="Two-month extension granted to 15 Oct 2026"
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" disabled={busy || !reason.trim()}>
            {override.isPending ? 'Recording…' : 'Record and proceed'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setReason('')
              setOverriding(false)
            }}
          >
            Cancel
          </Button>
          <p className="m-0 w-full text-micro text-muted">
            This does not correct the date and does not sign it. If an extension moved
            the deadline, correct the date instead — that is the honest record, and this
            is not a substitute for it.
          </p>
        </form>
      ) : null}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

function RunPicker({
  runs,
  isPending,
  error,
  selectedRunId,
  matterId,
}: {
  runs: PipelineRunSummary[]
  isPending: boolean
  error: unknown
  selectedRunId: string | null
  matterId: string
}) {
  const router = useRouter()

  return (
    <Card role="group" aria-label="Every run on this matter" className="gap-3">
      <CardHeader
        title="Every run on this matter"
        description="Newest first. A run is never edited — a second opinion is a second run, so this is the history of what was asked and what came back."
      />
      {isPending ? (
        <LoadingLine label="Loading runs…" />
      ) : error ? (
        <RunErrorCard title="Could not load this matter's runs" error={error} flush />
      ) : runs.length === 0 ? (
        <p className="m-0 text-body text-muted">No runs on this matter.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {runs.map((summary) => (
            <li key={summary.id}>
              <a
                href={router.href(`/case/${matterId}/${summary.id}`)}
                aria-current={summary.id === selectedRunId ? 'true' : undefined}
                className={cn(
                  'flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-meta no-underline',
                  summary.id === selectedRunId
                    ? 'border-brand bg-brand-100 text-ink'
                    : 'border-divider text-ink hover:bg-[#fafafa]',
                )}
              >
                <span className="font-mono text-micro text-muted">{summary.id.slice(0, 8)}</span>
                {/*
                  A failed run has no `status_line` — the runner never produced
                  one — so without the badge below the row was blank text next
                  to a duration, indistinguishable from a finished run whose
                  sentence happened to be short.
                */}
                <span className="mr-auto">
                  {summary.status_line || <span className="text-muted">no status line recorded</span>}
                </span>
                {summary.abandoned ? (
                  <Badge tone="warn">{ABANDONED_COPY.label}</Badge>
                ) : summary.status !== 'finished' ? (
                  <Badge tone={summary.status === 'failed' ? 'block' : 'accent'}>
                    {summary.status}
                  </Badge>
                ) : null}
                {/*
                  Not "dry run": a summary carries no stage strip, so a run the
                  platform refused before s5 and a run nobody asked to draft are
                  indistinguishable here. `generated` means one thing — a
                  completion was spent — and that is all this says.
                */}
                <Badge tone={summary.generated ? 'accent' : 'neutral'}>
                  {summary.generated ? RUN_ROW_LABELS.called : RUN_ROW_LABELS.not_called}
                </Badge>
                {summary.gate_block_count > 0 ? (
                  <Badge tone="block">{summary.gate_block_count} BLOCK</Badge>
                ) : null}
                <span className="text-micro text-muted">{formatWhen(summary.started_at)}</span>
                <span className="text-micro text-muted">{formatDuration(summary.duration_ms)}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

/**
 * Everything one run did — the work first, the machinery under a disclosure.
 *
 * The order used to be the runner's: verdict, stages, deadline, retrieval,
 * ledger, gate, worklist, and only then the draft a person actually came to
 * read. That is the right order for auditing a refusal and the wrong one for
 * doing the job, and an attorney opening this page for the first time met six
 * cards of internals before anything addressed to them.
 *
 * So the draft, the list of what stands between it and the post, and the client
 * pack come first; the six diagnostic cards keep every word they had, one click
 * away. Collapsed is not hidden — `<details>` keeps them in the document and in
 * the accessibility tree, findable by search and by a screen reader, which is
 * the property that matters for a record whose whole purpose is that a refusal
 * can be inspected later.
 */
function RunReport({ run, jurisdiction }: { run: PipelineRun; jurisdiction?: Jurisdiction }) {
  return (
    <>
      {run.abandoned ? <AbandonedCard /> : null}

      {/* What the attorney came for. */}
      <DraftCard run={run} />
      <WorklistCard run={run} />
      <PackCard matterId={run.matter_id} runId={run.id} />

      {/* What the pipeline did to get there. */}
      <TechnicalRecord run={run} jurisdiction={jurisdiction} />
    </>
  )
}

/**
 * The engine's own account of the run, intact, behind one click.
 *
 * Every card below is unchanged — this adds a lid, not an edit. The summary
 * says what is inside in the reader's terms so that opening it is a choice
 * somebody makes on purpose rather than a search through unlabelled internals.
 */
function TechnicalRecord({ run, jurisdiction }: { run: PipelineRun; jurisdiction?: Jurisdiction }) {
  return (
    <details className="rounded-lg border border-divider bg-surface">
      <summary className="cursor-pointer list-none px-[18px] py-3.5">
        <span className="flex items-baseline gap-2 text-meta font-medium text-ink">
          <span aria-hidden="true" className="text-muted">
            ▸
          </span>
          Technical record — every check the pipeline ran, in order
        </span>
        <span className="mt-0.5 block pl-5 text-micro font-normal text-muted">
          Stage outcomes, both deadline dates side by side, what the law corpus returned, and
          every gate finding. You do not need any of it to act on this matter — it is here so a
          refusal can always be traced back to the rule that made it.
        </span>
      </summary>

      <div className="flex flex-col gap-4 border-t border-divider p-[18px]">
        <VerdictCard run={run} />
        <StagesCard stages={run.stages} />
        <DeadlineCard reconciliation={run.reconciliation} jurisdiction={jurisdiction} />
        <BundleCard bundle={run.bundle} />
        <LedgerCard runId={run.id} />
        <GateCard findings={run.gate_findings} verdict={run.verdict} />
      </div>
    </details>
  )
}

/**
 * A run whose request went away, said plainly and said first.
 *
 * It is drawn above the verdict card rather than inside it because everything
 * below it is a report on a run that stopped part-way: empty stages, no
 * verdict, no draft. Without this card that reads as a pipeline that produced
 * nothing, and the operator goes looking for a bug in the drafting stages
 * instead of re-running the matter.
 *
 * Not styled as an error. Nothing failed — persist-first wrote this row on
 * purpose so a lost request would be visible instead of vanishing, and the row
 * is the mechanism working, not a symptom.
 */
function AbandonedCard() {
  return (
    <Card
      role="alert"
      aria-label="Abandoned run"
      // `warn-300`/`warn-50` are not tokens this palette defines — the tone
      // scale is `warn-bg`/`warn-fg`/`warn-line` (tailwind.config.js) — so the
      // card that most needed to look different rendered identical to every
      // other one. Tailwind drops an unknown utility silently, which is exactly
      // how a warning loses its colour without anything failing.
      className="gap-2 border-warn-line bg-warn-bg"
    >
      <CardHeader
        kicker={ABANDONED_COPY.label}
        title={ABANDONED_COPY.headline}
        description={ABANDONED_COPY.detail}
      />
    </Card>
  )
}

/**
 * The verdict, the status line verbatim, and whether this may be exported.
 *
 * `status_line` is printed as the runner wrote it, not reworded: its leading
 * word is the verdict the genre harness judges on, and a `not_built` line names
 * the file that will implement the missing port. Rewriting it would delete the
 * one actionable thing in it.
 */
function VerdictCard({ run }: { run: PipelineRun }) {
  const copy = run.verdict ? VERDICT_COPY[run.verdict] : null
  // `not_built` is also how a deliberate dry run ends; the gloss for a missing
  // port ("has not landed") told a tester the build was incomplete.
  const dryRun = run.verdict === 'not_built' && modelCallState(run) === 'dry_run'

  return (
    <Card role="group" aria-label="How this run ended" className="gap-3">
      <CardHeader
        kicker={`run ${run.id.slice(0, 8)}`}
        title={dryRun ? 'dry run' : (run.verdict ?? run.status)}
        description={
          dryRun
            ? 'No model was called, as asked. Everything before the draft ran, and the status line below says what was prepared.'
            : copy?.gloss
        }
        actions={
          run.exportable ? (
            <Badge tone="ok">exportable</Badge>
          ) : (
            <Badge tone={run.verdict === 'blocked' ? 'block' : 'neutral'}>not exportable</Badge>
          )
        }
      />

      <p className="m-0 rounded-md bg-ground px-3 py-2 font-mono text-meta">{run.status_line}</p>

      <KeyValues
        items={[
          {
            label: 'Status',
            // The pair, not just the column. `running` is what the row says;
            // `abandoned` is whether anyone still believes it, and showing the
            // first without the second is the whole bug in one field.
            value: run.abandoned ? `${run.status} · ${ABANDONED_COPY.label}` : run.status,
          },
          // One definition, shared with DraftCard below. `generated` answers
          // "was a completion spent", not "did you ask for one" — asserting
          // "dry run" off it alone told a deadline-blocked run to switch on a
          // toggle that was already on, and contradicted the abandoned notice
          // on this very screen. See `modelCallState`.
          { label: 'Model call', value: MODEL_CALL_COPY[modelCallState(run)] },
          { label: 'Office', value: run.office ?? '—' },
          { label: 'Grounds', value: run.grounds.length ? run.grounds.join(' · ') : 'none supplied' },
          { label: 'Kind', value: run.kind ?? '—' },
          { label: 'Attempts', value: String(run.attempts) },
          { label: 'Started', value: formatWhen(run.started_at) },
          { label: 'Duration', value: formatDuration(run.duration_ms) },
          ...(run.decline_reason
            ? [{ label: 'Decline reason', value: <Badge tone="neutral">{run.decline_reason}</Badge> }]
            : []),
        ]}
      />

      {run.error ? (
        <p role="alert" className="m-0 rounded-md border border-block-line bg-block-bg px-3 py-2 text-micro text-block-fg">
          {run.error}
        </p>
      ) : null}
    </Card>
  )
}

/**
 * The stage list, in the order the runner walked it.
 *
 * The array is rendered as it arrived — this is a record of a sequence, and
 * sorting it by anything would destroy the only information a reader needs to
 * answer "where did it stop". A stage that is absent stopped nothing: the run
 * never reached it, which the last row's outcome explains.
 */
function StagesCard({ stages }: { stages: PipelineStageResult[] }) {
  return (
    <Card role="group" aria-label="What ran, step by step" className="gap-3">
      <CardHeader
        title="What ran, step by step"
        description="In the order they happened. The list stops where the run stopped — anything below the last row was never reached."
      />
      {stages.length === 0 ? (
        <p className="m-0 text-body text-muted">This run recorded no stages.</p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Stage</TH>
              <TH>Outcome</TH>
              <TH>What it found</TH>
            </TR>
          </THead>
          <TBody>
            {stages.map((stage) => (
              <TR key={stage.stage}>
                <TD>
                  <StageName id={stage.stage} />
                </TD>
                <TD>
                  <Badge tone={OUTCOME_TONE[stage.outcome] ?? 'neutral'}>{stage.outcome}</Badge>
                </TD>
                <TD>
                  <p className="m-0 text-meta">{stage.detail}</p>
                  <StageData data={stage.data} />
                  {stage.considerations.map((line) => (
                    <p key={line} className="m-0 mt-1 text-micro text-muted">
                      CONSIDER · {line}
                    </p>
                  ))}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  )
}

/**
 * A stage's plain name over its gloss, with the stage id kept in a tooltip.
 *
 * `stageGloss` answers with the id itself for a stage it does not know, so the
 * gloss line is dropped in that case: otherwise an unmapped stage would print
 * its id twice, and a mapped one with no gloss would print it bare.
 */
function StageName({ id }: { id: string }) {
  const gloss = stageGloss(id)
  return (
    <>
      <div className="text-meta" title={id}>
        {stageLabel(id)}
      </div>
      {gloss !== id ? <div className="text-micro text-muted">{gloss}</div> : null}
    </>
  )
}

/**
 * The structured half of a stage result.
 *
 * Rendered because `detail` is a sentence for a person and `data` is the same
 * finding in the numbers that produced it — coverage ratios, vowel ratios,
 * scores. When the two disagree the numbers are the evidence, and hiding them
 * would make the sentence unfalsifiable.
 */
function StageData({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data)
  if (entries.length === 0) return null
  return (
    <p className="m-0 mt-1 font-mono text-micro text-muted">
      {entries.map(([key, value]) => `${key}=${renderScalar(value)}`).join(' · ')}
    </p>
  )
}

function renderScalar(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (Array.isArray(value)) return value.map((item) => renderScalar(item)).join(',')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// ---------------------------------------------------------------------------
// The deadline (I7)
// ---------------------------------------------------------------------------

/**
 * Both dates, always, side by side.
 *
 * The printed date and the computed date are separate rows because they are
 * separate sources: what a person read off the papers, and what the rule engine
 * computed from the report date. Showing only the date the matter is worked to
 * would turn a two-source cross-check back into a single unchecked number,
 * which is the failure I7 exists to prevent and the one that produced the first
 * live draft's wrong deadline.
 */
/**
 * The rule that computed a date, in words: "Confirmed India TMR rule". The
 * status stays in the sentence because it is what a person weighs the date by
 * (a provisional rule's date is a cross-check, not a settled one); the rule's
 * own id is for whoever is debugging a date, so the caller keeps it in a
 * tooltip. The office is the matter's, and is left out while the matter loads.
 */
function ruleName(status: string | null, jurisdiction: Jurisdiction | undefined): string {
  const office = jurisdiction ? (JURISDICTION_OFFICES[jurisdiction] ?? jurisdiction) : null
  const words = [status?.toLowerCase(), office, 'rule'].filter(Boolean).join(' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function DeadlineCard({
  reconciliation,
  jurisdiction,
}: {
  reconciliation: DeadlineReconciliation | null
  jurisdiction?: Jurisdiction
}) {
  if (!reconciliation) {
    return (
      <Card role="group" aria-label="The two deadline dates" className="gap-2">
        <CardHeader
          title="The two deadline dates"
          description="This run did not reach the deadline stage, so no cross-check exists. That is not the same as the dates agreeing."
        />
      </Card>
    )
  }

  return (
    <Card role="group" aria-label="The two deadline dates" className="gap-3">
      <CardHeader
        title="The two deadline dates"
        description="The rule engine computes its own date and flags any disagreement — the entry from the papers is one of two sources, never the only one."
        actions={
          reconciliation.blocks ? (
            <Badge tone="block">BLOCK</Badge>
          ) : reconciliation.outcome === 'AGREED' ? (
            <Badge tone="ok">{reconciliation.outcome}</Badge>
          ) : (
            <Badge tone="warn">{reconciliation.outcome}</Badge>
          )
        }
      />
      <KeyValues
        items={[
          { label: 'On the papers', value: formatDate(reconciliation.printed_date) },
          { label: 'Rule computed', value: formatDate(reconciliation.computed_date) },
          { label: 'Worked to', value: formatDate(reconciliation.deadline_date) },
          {
            label: 'Rule',
            value: reconciliation.rule_id ? (
              <span
                title={`Rule ${reconciliation.rule_id}`}
                className="cursor-help text-meta underline decoration-dotted underline-offset-2"
              >
                {ruleName(reconciliation.rule_status, jurisdiction)}
              </span>
            ) : (
              'no rule for this office'
            ),
          },
          {
            label: 'Flags',
            value: reconciliation.flags.length ? (
              <span className="flex flex-wrap gap-1.5">
                {reconciliation.flags.map((flag) => (
                  <Badge key={flag} tone={reconciliation.blocks ? 'block' : 'warn'}>
                    {flag}
                  </Badge>
                ))}
              </span>
            ) : (
              'none'
            ),
          },
        ]}
      />
      <p className="m-0 text-micro text-muted">
        {reconciliation.blocks
          ? 'A blocking flag stops the run before any draft is generated. The only way past it is a person deciding which date is right and recording that decision.'
          : 'No blocking flag. A PROVISIONAL rule still computes and still cross-checks — it records that the legal value has not been confirmed by someone who can be held to it.'}
      </p>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Retrieval
// ---------------------------------------------------------------------------

/**
 * What the corpus gave this draft — including what it did not.
 *
 * Every block is a row even when it is empty. An empty block is not an error
 * and not a warning: a matter with no cited authorities legitimately has an
 * empty `authorities` block. It is a fact about this draft the reviewer needs,
 * because the alternative is a draft that is thin for a reason nobody can see
 * (`server/app/drafting/bundle.py`) — and each one becomes a CONSIDER line on
 * the worklist below.
 */
function BundleCard({ bundle }: { bundle: RetrievalBundleSummary | null }) {
  if (!bundle) {
    return (
      <Card role="group" aria-label="What the law library gave us" className="gap-2">
        <CardHeader
          title="What the law library gave us"
          description="This run did not reach the research stage, so the corpus contributed nothing to it."
        />
      </Card>
    )
  }

  const rows = orderedBlocks(bundle.blocks)
  const emptyCount = rows.filter(([, block]) => block.empty).length

  return (
    <Card role="group" aria-label="What the law library gave us" className="gap-3">
      <CardHeader
        title="What the law library gave us"
        description="One row per prompt block. An empty block is shown as empty, never omitted."
        actions={
          bundle.vector_led ? (
            <Badge tone="ok">vector-led</Badge>
          ) : (
            <Badge tone="warn">outcome-weighted</Badge>
          )
        }
      />
      <Table>
        <THead>
          <TR>
            <TH>Block</TH>
            <TH align="right">Entries</TH>
            <TH>State</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map(([name, block]) => (
            <TR key={name}>
              <TD>
                <span className="font-mono text-meta">{name}</span>
              </TD>
              <TD align="right">{block.count}</TD>
              <TD>
                {block.empty ? (
                  <Badge tone="warn">empty</Badge>
                ) : (
                  <Badge tone="neutral">filled</Badge>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <p className="m-0 text-micro text-muted">
        {bundle.source_ids} distinct {bundle.source_ids === 1 ? 'source' : 'sources'} contributed
        {emptyCount > 0
          ? ` · ${emptyCount} empty ${emptyCount === 1 ? 'block' : 'blocks'}, each a CONSIDER line on the worklist`
          : ' · no empty blocks'}
        .
        {bundle.vector_led
          ? ''
          : ' The search fell back to ranking by past outcomes because the embedding model was unavailable — not the setup drafting quality was measured with.'}
      </p>
    </Card>
  )
}

/**
 * `bundle.source_ids` resolved back into the documents they name (I2 / REQ-21).
 *
 * `BundleCard` above only ever shows a count — the stored bundle keeps just
 * the `custom_id` list (`_bundle_snapshot`, `server/app/pipeline/pipeline_runs.py`),
 * never the citation text — so this is the one place "which document is that,
 * and can I open it" gets answered, via `GET
 * /api/pipeline/runs/{run_id}/ledger`, which re-resolves each id live through
 * the same `resolve_source` the source router itself uses.
 *
 * A `custom_id` that failed to resolve renders in the same table, in the same
 * row shape, as one that did: `unusable` is a fact about the corpus (a
 * missing volume, a stale row), never a broken link, and hiding those rows
 * would be this screen re-implementing the silent failure I2 was written to
 * rule out.
 */
function LedgerCard({ runId }: { runId: string }) {
  const { hasScope } = useAuth()
  const canRead = hasScope('research:read')
  const { data: ledger, isLoading, error } = useRunLedger(canRead ? runId : null)

  if (!canRead) {
    return (
      <Card role="group" aria-label="Where each citation came from" className="gap-2">
        <CardHeader
          title="Where each citation came from"
          description="Resolving this run's sources back to their documents needs the research:read scope, which this credential does not hold."
        />
      </Card>
    )
  }

  return (
    <Card role="group" aria-label="Where each citation came from" className="gap-3">
      <CardHeader
        title="Where each citation came from"
        description="Every source the retrieval bundle drew on, resolved back to the document it came from."
      />
      {isLoading ? (
        <LoadingLine label="Resolving sources…" />
      ) : error ? (
        <RunErrorCard title="Could not load the citations" error={error} flush />
      ) : !ledger || ledger.entries.length === 0 ? (
        <p className="m-0 text-body text-muted">
          No sources to resolve — this run's bundle is empty.
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Source</TH>
              <TH>Cite</TH>
              <TH>Office</TH>
              <TH>State</TH>
            </TR>
          </THead>
          <TBody>
            {ledger.entries.map((entry) => (
              <TR key={entry.custom_id}>
                <TD>
                  <span className="font-mono text-meta">{entry.custom_id}</span>
                </TD>
                <TD>{entry.cite ?? entry.title ?? '—'}</TD>
                <TD>{entry.office ?? '—'}</TD>
                <TD>
                  {entry.openable ? (
                    <a
                      href={`${API_BASE}/api/source/${encodeURIComponent(entry.custom_id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-meta text-brand-700 no-underline hover:underline"
                    >
                      open source
                    </a>
                  ) : (
                    <Badge tone={entry.status === 'unusable' ? 'warn' : 'neutral'}>
                      {entry.status}
                    </Badge>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// The gate (I4)
// ---------------------------------------------------------------------------

/**
 * Every finding, BLOCK first.
 *
 * Grouped by severity rather than sorted into one list so that "is anything
 * stopping this" is answered by whether the first group exists, and so a WARN
 * can never be mistaken for a BLOCK by sitting next to one. The codes are
 * `tm_gate`'s own spellings and are printed verbatim — they are what the rule
 * is called in the code that fired it, and a friendlier name would not be
 * greppable.
 */
function GateCard({
  findings,
  verdict,
}: {
  findings: GateFinding[]
  verdict: PipelineRun['verdict']
}) {
  const blocks = findings.filter((finding) => finding.severity === 'BLOCK')

  return (
    <Card role="group" aria-label="What the checks found" className="gap-3">
      <CardHeader
        title="What the checks found"
        description="Deterministic rules over the draft text: every raised ground answered, every citation known, no fact asserted that nobody supplied, no placeholder left standing."
        actions={
          blocks.length > 0 ? (
            <Badge tone="block">
              {blocks.length} BLOCK {blocks.length === 1 ? 'open' : 'open'}
            </Badge>
          ) : findings.length > 0 ? (
            <Badge tone="warn">{findings.length} to review</Badge>
          ) : (
            <Badge tone="ok">gate clear</Badge>
          )
        }
      />

      {findings.length === 0 ? (
        <p className="m-0 text-body text-muted">
          {verdict && ['drafted', 'blocked'].includes(verdict)
            ? 'No findings. The gate ran over this draft and had nothing to say.'
            : 'No findings — this run produced no draft for the gate to read.'}
        </p>
      ) : (
        GATE_SEVERITIES.map((severity) => {
          const group = findings.filter((finding) => finding.severity === severity)
          if (group.length === 0) return null
          return (
            <div key={severity} className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <Badge tone={SEVERITY_TONE[severity]}>{severity}</Badge>
                <span className="text-micro text-muted">
                  {severity === 'BLOCK'
                    ? 'stops export until it is resolved or overridden with a recorded reason'
                    : severity === 'WARN'
                      ? 'goes to the reviewer’s worklist'
                      : 'an observation the reviewer may ignore'}
                </span>
              </div>
              {group.map((finding, index) => (
                <div
                  key={`${finding.code}-${index}`}
                  className={cn(
                    'flex flex-col gap-0.5 rounded-md border px-3 py-2',
                    severity === 'BLOCK'
                      ? 'border-block-line bg-block-bg'
                      : severity === 'WARN'
                        ? 'border-warn-line bg-warn-bg'
                        : 'border-divider bg-ground',
                  )}
                >
                  <span className="font-mono text-micro">{finding.code}</span>
                  <span className="text-meta">{finding.detail}</span>
                </div>
              ))}
            </div>
          )
        })
      )}
    </Card>
  )
}

/**
 * The CONSIDER lines — the part of the reviewer's worklist P2 can already fill.
 *
 * Item 16's card has four kinds (STOP / VERIFY / SUPPLY / CONSIDER) and the
 * port that builds the other three has not landed. Rather than invent them,
 * this shows what the run actually produced and says which are missing. Nothing
 * here comes from a model: every line derives from a gate finding, a marker,
 * retrieval coverage or the objection's kind, so two runs over the same draft
 * produce the same card — a worklist must not shimmer between reads.
 */
/**
 * The reviewer's worklist — what has to happen before this draft goes out.
 *
 * Four actions in the order they are done, each line naming the exact string
 * to search the draft for and the rule that emitted it. Nothing here was
 * generated: every line derives from a gate finding, a marker in the draft,
 * retrieval coverage, the deadline reconciliation or the objection's kind, so
 * two runs over the same draft produce the same card and any line can be
 * traced back to a rule.
 *
 * The total is rendered through `formatWorklistEffort`, which refuses to print
 * a bare number while the server says the minutes are uncalibrated. The
 * prototype's measured timings are not in this repository; presenting an
 * estimate as a measurement on the way to the screen is precisely the I9
 * failure this project keeps catching in its own output.
 */
function WorklistCard({ run }: { run: PipelineRun }) {
  const { worklist } = run
  const groups = WORKLIST_ACTIONS.map(
    (action) => [action, worklist.items.filter((item) => item.action === action)] as const,
  ).filter(([, items]) => items.length > 0)

  return (
    <Card
      id={CASE_ANCHORS.worklist}
      tabIndex={-1}
      role="group"
      aria-label="What needs doing before this goes out"
      className="gap-3"
    >
      <CardHeader
        title="What needs doing before this goes out"
        description="Every line traces back to something the checks found — a citation, a ground left unanswered, a fact only your client has. Nothing here was invented to fill the list."
        actions={
          worklist.items.length > 0 ? (
            <span className="text-meta text-muted">{formatWorklistEffort(worklist)}</span>
          ) : null
        }
      />

      {worklist.items.length === 0 ? (
        <p className="m-0 text-body text-muted">
          Nothing on the worklist for this run. That is a conclusion, not a gap — the gate, the
          deadline and retrieval each reported nothing to act on.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map(([action, items]) => (
            <section key={action} className="flex flex-col gap-1.5">
              <h5 className="m-0 flex items-center gap-2 text-meta">
                <Badge tone={ACTION_TONE[action]}>{action}</Badge>
                <span className="font-normal text-muted">{ACTION_COPY[action]}</span>
              </h5>
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {items.map((item) => (
                  <li
                    key={`${item.code}:${item.detail}`}
                    className="flex flex-col gap-0.5 rounded-md border border-divider px-3 py-2"
                  >
                    <span className="flex flex-wrap items-baseline gap-2 text-meta">
                      {/* The rule's id is for whoever is debugging, so it is the
                          tooltip. It stays on screen only when no sentence
                          explains the line, so a row is never a bare literal. */}
                      {item.gloss ? null : (
                        <code className="font-mono text-micro text-muted">{item.code}</code>
                      )}
                      <span className="font-medium" title={item.code}>
                        {item.detail}
                      </span>
                      <span className="ml-auto text-micro text-muted" title={`Source: ${item.source}`}>
                        {item.minutes} min
                      </span>
                    </span>
                    {item.gloss ? (
                      <span className="text-micro text-muted">{item.gloss}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {run.considerations.length > 0 ? (
        <details className="text-micro text-muted">
          <summary className="cursor-pointer">
            The runner’s own notes ({run.considerations.length})
          </summary>
          <ul className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0">
            {run.considerations.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------

/**
 * The draft, and whether it may leave the building.
 *
 * A blocked draft is shown, in full, under a red banner that says it cannot be
 * exported. Both halves matter: hiding it would destroy work product a reviewer
 * needs to read, and showing it without the banner would let a correct-looking
 * letter be copied out of the screen past a gate that refused it. There is no
 * override control — an override is a recorded event with a reason and an
 * actor (I4, I12), and it belongs to P5's workbench rather than to a button
 * here that would make the refusal look like a dismissible dialog.
 */
function DraftCard({ run }: { run: PipelineRun }) {
  const blocks = run.gate_findings.filter((finding) => finding.severity === 'BLOCK').length

  if (!run.draft) {
    return (
      <Card role="group" aria-label="Draft" className="gap-2">
        <CardHeader title="Draft" description="There is no draft on this run." />
        <p className="m-0 text-body text-muted">{NO_DRAFT_COPY[modelCallState(run)]}</p>
        <p className="m-0 text-micro text-muted">{run.status_line}</p>
      </Card>
    )
  }

  return (
    <Card role="group" aria-label="Draft" className="gap-3">
      <CardHeader
        title="Draft"
        description={`Variant as run · ${run.grounds.length} ${run.grounds.length === 1 ? 'ground' : 'grounds'} · ${run.attempts} ${run.attempts === 1 ? 'attempt' : 'attempts'}`}
        actions={
          run.exportable ? (
            <Badge tone="ok">exportable</Badge>
          ) : (
            <Badge tone="block">not exportable</Badge>
          )
        }
      />

      {!run.exportable ? (
        <div
          role="status"
          className="flex flex-col gap-1 rounded-md border border-block-line bg-block-bg px-3 py-2 text-meta text-block-fg"
        >
          <strong className="font-semibold">
            Export refused: {blocks} negative-gate {blocks === 1 ? 'BLOCK' : 'BLOCKs'} open.
          </strong>
          <span>
            The draft is kept because it is work product. Nothing downstream may send it until every
            BLOCK is resolved, or overridden with a reason that is recorded against this matter.
          </span>
        </div>
      ) : null}

      <DraftView run={run} maxHeightClass="max-h-[520px]" />
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Assessment pack — chunk 8. Render + lint, then a manual approve/dispatch.
// ---------------------------------------------------------------------------

/**
 * The offer email and tiered client questions over this run, linted, then a
 * **manual** approve/dispatch. Small on purpose, matching the docket's own
 * "P6 · small" sizing and `LedgerCard`'s precedent for how much a single
 * card should carry.
 *
 * Nothing here sends anything (I8's sibling rule for this chunk): "record
 * dispatch" writes an event naming who this was sent to from the reviewer's
 * own mail client, and the print view is the only thing with a body to
 * copy. `pack.approved`/`pack.dispatched` are the server's own derived
 * answer on every read, never tracked here, so a page left open across an
 * approval elsewhere still shows the truth on the next fetch.
 */
function PackCard({ matterId, runId }: { matterId: string; runId: string }) {
  const { hasScope } = useAuth()
  const canWrite = hasScope('matters:write')
  const pack = useMatterPack(canWrite ? matterId : null)
  const render = useRenderPack()
  const approve = useApprovePack()
  const dispatch = useDispatchPack()
  const [officeAction, setOfficeAction] = useState('')
  const [recipients, setRecipients] = useState('')

  if (!canWrite) {
    return (
      <Card role="group" aria-label="Offer email for your client" className="gap-2">
        <CardHeader
          title="Offer email for your client"
          description="Writing the offer email and the questions for your client needs the matters:write permission, which this login does not have."
        />
      </Card>
    )
  }

  const notRenderedYet = isApiError(pack.error) && pack.error.isNotFound
  const printHref = `${API_BASE}/api/matters/${encodeURIComponent(matterId)}/pack/print`

  function handleRender(event: FormEvent) {
    event.preventDefault()
    render.mutate({ matterId, body: { office_action: officeAction } })
  }

  function handleDispatch(event: FormEvent) {
    event.preventDefault()
    const list = recipients
      .split(/[,\n]/)
      .map((entry) => entry.trim())
      .filter(Boolean)
    dispatch.mutate({ matterId, recipients: list })
  }

  return (
    <Card role="group" aria-label="Offer email for your client" className="gap-3">
      <CardHeader
        title="Offer email for your client"
        description="A fee offer and the questions only your client can answer. You read it, approve it and send it — this screen never sends anything on its own."
        actions={
          <span className="text-micro text-muted">run {runId.slice(0, 8)}</span>
        }
      />

      {pack.isPending ? <LoadingLine label="Checking whether one has been written…" /> : null}
      {pack.isError && !notRenderedYet ? (
        <RunErrorCard title="Could not load the offer email" error={pack.error} flush />
      ) : null}

      {!pack.data ? (
        <form className="flex flex-col gap-2" onSubmit={handleRender}>
          <Field
            label="Paste the office action"
            hint="We do not keep your client’s document after a run finishes, so it has to be pasted again here."
          >
            {({ id }) => (
              <Textarea
                id={id}
                required
                rows={4}
                value={officeAction}
                onChange={(event) => setOfficeAction(event.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" loading={render.isPending} loadingLabel="Writing it…">
            Create the offer email
          </Button>
          {render.isError ? (
            <p role="alert" className="m-0 text-micro text-block-fg">
              {errorMessage(render.error)}
            </p>
          ) : null}
        </form>
      ) : (
        <PackBody
          matterId={matterId}
          pack={pack.data}
          approve={approve}
          dispatch={dispatch}
          recipients={recipients}
          onRecipientsChange={setRecipients}
          onDispatch={handleDispatch}
          printHref={printHref}
        />
      )}
    </Card>
  )
}

function PackBody({
  matterId,
  pack,
  approve,
  dispatch,
  recipients,
  onRecipientsChange,
  onDispatch,
  printHref,
}: {
  matterId: string
  pack: AssessmentPack
  approve: ReturnType<typeof useApprovePack>
  dispatch: ReturnType<typeof useDispatchPack>
  recipients: string
  onRecipientsChange: (value: string) => void
  onDispatch: (event: FormEvent) => void
  printHref: string
}) {
  return (
    <div className="flex flex-col gap-3">
      <KeyValues
        items={[
          { label: 'Fee', value: pack.offer.fee },
          { label: 'Grounds', value: pack.offer.grounds.join(' · ') || '—' },
        ]}
      />

      <div>
        <h5 className="m-0 mb-1 text-meta">Problems found in this email ({pack.lint.length})</h5>
        {pack.lint.length === 0 ? (
          <p className="m-0 text-body text-muted">Clean — nothing flagged.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {pack.lint.map((finding) => (
              <li
                key={`${finding.code}:${finding.detail}`}
                className="rounded-md border border-warn-line bg-warn-bg px-2 py-1 text-micro text-warn-fg"
              >
                <code className="font-mono">{finding.code}</code> — {finding.detail}
              </li>
            ))}
          </ul>
        )}
      </div>

      <a
        href={printHref}
        target="_blank"
        rel="noopener noreferrer"
        className="text-meta text-brand-700 no-underline hover:underline"
      >
        Open a printable version →
      </a>

      <div className="flex flex-col gap-1">
        <Button
          variant="secondary"
          disabled={pack.approved}
          loading={approve.isPending}
          loadingLabel="Approving…"
          onClick={() => approve.mutate(matterId)}
        >
          {pack.approved ? 'Approved' : 'Approve for sending'}
        </Button>
        {approve.isError ? (
          <p role="alert" className="m-0 text-micro text-block-fg">
            {errorMessage(approve.error)}
          </p>
        ) : null}
      </div>

      <form className="flex flex-col gap-2" onSubmit={onDispatch}>
        <Field
          label="Recipients"
          hint="One per line or comma-separated. Recorded only — this screen sends nothing; dispatch is a record of a send that already happened."
        >
          {({ id }) => (
            <Textarea
              id={id}
              rows={2}
              disabled={!pack.approved || pack.dispatched}
              value={recipients}
              onChange={(event) => onRecipientsChange(event.target.value)}
            />
          )}
        </Field>
        <Button
          type="submit"
          variant="secondary"
          disabled={!pack.approved || pack.dispatched}
          loading={dispatch.isPending}
          loadingLabel="Recording…"
        >
          {pack.dispatched ? 'Dispatched' : 'Record dispatch'}
        </Button>
        {dispatch.isError ? (
          <p role="alert" className="m-0 text-micro text-block-fg">
            {errorMessage(dispatch.error)}
          </p>
        ) : null}
      </form>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The log (I12)
// ---------------------------------------------------------------------------

/**
 * The matter's event log, in append order.
 *
 * Sorted by `id` rather than by `ts`: the id is the order the rows were
 * appended and the log is append-only, so it is the one ordering that cannot
 * disagree with what happened. Two events written inside one transaction can
 * share a timestamp, and sorting those by time would shuffle them differently
 * between reads.
 */
function EventLogCard({
  matter,
  isPending,
}: {
  matter: MatterDetail | undefined
  isPending: boolean
}) {
  const events = useMemo<MatterEvent[]>(
    () => [...(matter?.events ?? [])].sort((left, right) => left.id - right.id),
    [matter],
  )

  return (
    <Card role="group" aria-label="Event log" className="gap-3">
      <CardHeader
        kicker="History — never edited or deleted"
        title="Timeline"
        description="Every step writes here and nothing rewrites it. Who did each thing comes from the signed-in account, never from a name someone typed in."
      />
      {isPending ? (
        <LoadingLine label="Loading the log…" />
      ) : events.length === 0 ? (
        <p className="m-0 text-body text-muted">No events on this matter yet.</p>
      ) : (
        <div className="flex flex-col gap-1">
          {events.map((event) => (
            <div
              key={event.id}
              className="grid grid-cols-[150px_150px_1fr] items-baseline gap-2 border-b border-divider py-1 last:border-b-0"
            >
              <span className="text-micro tabular-nums text-muted">{formatWhen(event.ts)}</span>
              <span className="text-micro" title={event.stage}>
                {stageLabel(event.stage)}
              </span>
              <span className="text-meta">
                <span title={event.event}>{eventLabel(event.event, event.stage)}</span>
                {event.actor ? <span className="text-micro text-muted"> · {event.actor}</span> : null}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

export default CaseScreen
